import "~/css/style.css";

import moonSvg from "~/img/svg/moon.svg";
import sunSvg from "~/img/svg/sun.svg";
import { isMobile, isTablet } from "~/js/helpers";
import { Popover } from "~/js/utils/popover";

let coreDialogModulePromise;
const loadCoreDialog = () => {
	coreDialogModulePromise ??= import(/* webpackChunkName: "dialog-core" */ "~/js/utils/dialog.js");
	return coreDialogModulePromise;
};

async function initDialogMenu() {
	const openButton = document.querySelector("#openMenu");
	if (!openButton) return;
	const template = document.querySelector("#dialogMenu");
	if (!(template instanceof HTMLTemplateElement)) return;

	// Current page: the link with the longest matching path ("/" only on itself).
	const path = location.pathname;
	let current = null;
	for (const link of template.content.querySelectorAll('a[href^="/"]')) {
		const href = link.getAttribute("href");
		const matches = href === path || (href !== "/" && path.startsWith(href));
		if (matches && href.length > (current?.getAttribute("href").length ?? 0)) current = link;
	}
	current?.setAttribute("aria-current", "page");

	let dialog;
	let opening;
	openButton.addEventListener("click", async () => {
		if (opening) return;
		opening = (async () => {
			if (!dialog) {
				const { Dialog } = await loadCoreDialog();
				dialog = new Dialog({
					id: "navigationDialog",
					title: gettext("Navigazione"),
					variant: "drawer",
				});
			}
			await dialog.open(template);
		})();
		try {
			await opening;
		} catch {
			alert(gettext("Impossibile aprire la finestra. Ricarica la pagina e riprova."));
		} finally {
			opening = null;
		}
	});
}

// Dialog openers are delegated, so rows added after load (the reports index "Mostra altri") open them too.
// Each opener keeps its own controller, as before: reopening one reuses its dialog.
function delegateDialog(selector, load) {
	const openers = new WeakMap();
	const opening = new WeakSet();
	document.addEventListener("click", async (event) => {
		const openButton = event.target.closest(selector);
		if (!openButton || opening.has(openButton)) return;
		opening.add(openButton);
		try {
			let open = openers.get(openButton);
			if (!open) {
				open = await load(openButton);
				openers.set(openButton, open);
			}
			return await open();
		} catch {
			alert(gettext("Impossibile aprire la finestra. Ricarica la pagina e riprova."));
		} finally {
			opening.delete(openButton);
		}
	});
}

function initDialogUserReport() {
	let modulePromise;
	delegateDialog("[data-dialog='user-report']", async (openButton) => {
		modulePromise ??= import(
			/* webpackChunkName: "user-report-dialog" */ "~/js/dialogs/user-report.js"
		);
		const { createUserReportDialog } = await modulePromise;
		const controller = createUserReportDialog({ reportId: openButton.dataset.id || null });
		return async () => {
			const result = await controller.open();
			if (result.status === "submitted" && result.value?.action === "delete") {
				window.location.reload();
			}
		};
	});
}

function initDialogScheduleReport() {
	let modulePromise;
	delegateDialog("[data-dialog='schedule-report']", async (openButton) => {
		modulePromise ??= import(
			/* webpackChunkName: "schedule-report-dialog" */ "~/js/dialogs/schedule-report.js"
		);
		const { createScheduleReportDialog } = await modulePromise;
		const controller = createScheduleReportDialog({
			reportId: openButton.dataset.reportId || openButton.dataset.id || null,
			scheduleId: openButton.dataset.scheduleId || null,
		});
		return async () => {
			const result = await controller.open();
			// Pages listing schedule state (the reports index) refresh on this event.
			if (result.status === "submitted")
				document.dispatchEvent(new CustomEvent("galada:report-schedules-changed"));
		};
	});
}

function initLanguageDropdown() {
	const langDropdownBtn = document.querySelector("#language-dropdown");
	const languageDropdownTemplate = document.querySelector("#language-dropdown-template");
	if (!langDropdownBtn || !languageDropdownTemplate) return;

	const langDropdown = new Popover(langDropdownBtn, {
		trigger: "click",
		placement: "bottom-center",
	});
	langDropdown.setContent(languageDropdownTemplate);

	const langDropdownButtons = document.querySelectorAll("button[data-language]");
	if (!langDropdownButtons) return;

	for (const button of langDropdownButtons) {
		button.addEventListener("click", () => {
			const formData = new FormData();
			formData.append("language", button.dataset.language);

			fetch(window._galada.urls.setLanguage, {
				method: "POST",
				headers: {
					"X-CSRFToken": window._galada.csrfToken,
				},
				body: formData,
			}).then((response) => {
				if (response.ok) {
					window.location.reload(response.url);
				}
			});
		});
	}
}

function initSpinner() {
	const spinner = document.querySelector("#spinner-template");
	if (!spinner) return;

	window._galada.spinner = {
		defaultMessage: gettext("Caricamento in corso"),
		element: spinner,
		message: spinner.querySelector("span[data-message]"),
		show: (message = "") => {
			window._galada.spinner.message.textContent = message || window._galada.spinner.defaultMessage;
			spinner.classList.remove("hidden");
		},
		hide: () => {
			spinner.classList.add("hidden");
		},
	};
}

function initSwitchTheme() {
	const switchThemeBtn = document.querySelector("#switch-theme");
	if (!switchThemeBtn) return;

	const switchThemeIcon = (theme) => {
		return `<img src="${
			theme === "light" ? moonSvg : sunSvg
		}" class="pointer-events-none" alt="Switch theme" />`;
	};

	const toggleTheme = async () => {
		const theme = localStorage.getItem("theme");
		const newTheme = theme === "light" ? "dark" : "light";
		window.dispatchEvent(new CustomEvent("themeChange", { detail: { newTheme: newTheme } }));

		document.documentElement.classList.remove(theme);
		document.documentElement.classList.add(newTheme);
		switchThemeBtn.innerHTML = switchThemeIcon(newTheme);
		localStorage.setItem("theme", newTheme);
	};

	const theme = localStorage.getItem("theme");
	if (!theme) {
		const prefersDarkScheme = window.matchMedia("(prefers-color-scheme: dark)");
		localStorage.setItem("theme", prefersDarkScheme.matches ? "dark" : "light");
	}
	switchThemeBtn.innerHTML = switchThemeIcon(theme);
	switchThemeBtn.addEventListener("click", async () => {
		if (
			!document.startViewTransition ||
			window.matchMedia("(prefers-reduced-motion: reduce)").matches
		) {
			toggleTheme();
			return;
		}

		await document.startViewTransition(() => {
			toggleTheme();
		}).ready;
	});
}

function initUserDropdown() {
	const userDropdownBtn = document.querySelector("#user-dropdown");
	const userDropdownTemplate = document.querySelector("#user-dropdown-template");
	if (userDropdownBtn && userDropdownTemplate) {
		const userDropdown = new Popover(userDropdownBtn, {
			trigger: "click",
			placement: "bottom-end",
		});
		userDropdown.setContent(userDropdownTemplate);
	}
}

document.addEventListener("DOMContentLoaded", async () => {
	initDialogMenu();
	initDialogUserReport();
	initDialogScheduleReport();
	initLanguageDropdown();
	initSwitchTheme();
	initSpinner();
	initUserDropdown();

	window._galada = window._galada || {};
	window._galada.isMobile = isMobile();
	window._galada.isTablet = isTablet();
});
